import { CommonModule, NgIf } from '@angular/common';
import { Component, OnDestroy } from '@angular/core';
import { rowRemovingConfirmation } from '@asamco/application-link/shared';
import { AsamcoAuthService } from '@asamco/base';
import { fixOdataDateOffsetFilter, pathJoin, showErrorSwal } from '@asamco/utils';
import { Subject } from '@microsoft/signalr';
import { DxDataGridModule } from 'devextreme-angular';
import { LoadOptions } from 'devextreme/data';
import CustomStore from 'devextreme/data/custom_store';
import DataSource from 'devextreme/data/data_source';
import ODataStore from 'devextreme/data/odata/store';
import { EditorPreparedEvent, RowRemovingEvent } from 'devextreme/ui/data_grid';
import { Observable, catchError, firstValueFrom, map, of, throwError } from 'rxjs';
import { AlUserV2Service, UserDto } from '../../../services/al-user-v2.service';
import { AdminWarningBannerComponent } from '../admin-warning-banner.component';

type ViewModel = {
	state: 'loading'
} | {
	state: 'ready'
	dataSource: DataSource;
} | {
	state: 'error'
}

@Component({
	selector: 'app-shell-admin-users-management-grid',
	standalone: true,
	imports: [
		AdminWarningBannerComponent,
		CommonModule,
		DxDataGridModule,
		NgIf,
	],
	templateUrl: './admin-users-management-grid.component.html',
	styleUrl: './admin-users-management-grid.component.scss',
})
export class AdminUsersManagementGridComponent implements OnDestroy {
	private readonly destroy$ = new Subject<void>();

	public readonly viewModel$: Observable<ViewModel> = of({
		state: 'loading'
	}).pipe(
		map(() => {
			const viewModel: ViewModel = {
				state: 'ready',
				dataSource: this.buildDataSource(),
			};
			return viewModel;
		}),
		catchError(error => {
			showErrorSwal(error);
			const viewModel: ViewModel = {
				state: 'error'
			};
			return of(viewModel);
		}),
	);

	constructor(private asamcoAuthService: AsamcoAuthService, private userService: AlUserV2Service) {}

	ngOnDestroy(): void {
		this.destroy$.next();
		this.destroy$.complete();
	}

	private buildDataSource(): DataSource {

		const odataStore = new ODataStore({
			version: 4,
			key: 'id',
			keyType: 'Int64',
			url: pathJoin([this.asamcoAuthService.userSession.backend.odataBaseUrl, 'dynamic/_AL3_vw_Users']),
			beforeSend: this.asamcoAuthService.dxStoreBeforeSend,
			deserializeDates: false,
			onLoading: options => {
				fixOdataDateOffsetFilter(options.filter);
			},
		});
		// odataStore.insert = (values: any) => {
		// 	return fromPromise(
		// 		this.themeItemService
		// 			.createDraft({
		// 				themeId: this.themeId,
		// 				...values,
		// 			})
		// 			.pipe(
		// 				catchError(error => {
		// 					showErrorSwal(error);
		// 					return throwError('Er is een fout opgetreden');
		// 				})
		// 			)
		// 			.toPromise()
		// 	);
		// };
		// odataStore.insert = (values: unknown) => {
		// 	return Deferred();
		// }


		const customStore = new CustomStore<UserDto, number>({
			key: 'id',
			loadMode: 'processed',
			totalCount: (options: LoadOptions) => odataStore.totalCount(options),
			load: (options: LoadOptions) => {
				return odataStore.load(options);
			},
			byKey: (key) => odataStore.byKey(key),
			update: async (key: number, values: UserDto) => {
				return await firstValueFrom(this.userService.update({
					id: key,
					...values,
				}));
			},
			remove: async (key: number) => {
				await firstValueFrom(this.userService.delete(key).pipe(this.handleDataStoreError()));
			},
		});

		const dataSource = new DataSource<UserDto, number>({
			key: 'id',
			requireTotalCount: true,
			paginate: true,
			store: customStore,
		});
		return dataSource;
	}

	private handleDataStoreError<T>(): (source: Observable<T>) => Observable<T> {
		return (source: Observable<T>): Observable<T> =>
			source.pipe(
				catchError(error => {
					showErrorSwal(error)
					// Let Dx do its thing as well.
					return throwError(() => error);
				})
			);
	}

	public rowRemoving($event: RowRemovingEvent<UserDto>, viewModel: ViewModel): void {
		const user = $event.data;

		$event.cancel = rowRemovingConfirmation({
			text: `Delete user '${user.firstName} ${user.lastName} (${user.email})'?`,
		});
	}

	public onEditorPreparing(event: EditorPreparedEvent<UserDto>) {
		if (event.parentType !== "dataRow") {
			return;
		}

		if (event.row.isNewRow) {
			return;
		}

		if (['userName', 'firstName', 'lastName', 'email'].findIndex(field => field === event.dataField) !== -1) {
			return;
		}

		const editorOptions = (<any>event).editorOptions;
		editorOptions.disabled = true;
	}

}
